import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, Shield, Trophy, CalendarDays, Target, ListOrdered, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { NotificationBell } from "./NotificationBell";

const navItems = [
  { to: "/", icon: Trophy, key: "leagues" },
  { to: "/predict", icon: Target, key: "predict" },
  { to: "/matches", icon: CalendarDays, key: "matches" },
  { to: "/standings", icon: ListOrdered, key: "table" },
  { to: "/profile", icon: User, key: "profile" },
] as const;

export const AppHeader = ({ title }: { title?: string }) => {
  const { user, signOut, isSuperAdmin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-40 bg-gradient-hero text-white shadow-elevated">
      <div className="container flex h-16 items-center justify-between gap-4 px-4">
        <Link to="/" className="flex items-center gap-2 font-bold text-white">
          <img
            src="/icon-512.png?v=2"
            alt="Tipomatic"
            className="h-9 w-9 rounded-xl object-cover ring-1 ring-white/20"
          />
          <span className="text-base sm:text-lg truncate max-w-[160px] sm:max-w-none">
            {title ?? <>Tipomatic</>}
          </span>
        </Link>
        {user && (
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map(({ to, icon: Icon, key }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? "bg-white/20 text-white"
                      : "text-white/80 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span>{t(`nav.${key}`)}</span>
              </NavLink>
            ))}
          </nav>
        )}
        <div className="flex items-center gap-1 text-white">
          {isSuperAdmin && (
            <Button variant="ghost" size="icon" className="h-9 w-9 text-white hover:bg-white/15 hover:text-white" asChild aria-label={t("admin.title")}>
              <Link to="/admin"><Shield className="h-4 w-4" /></Link>
            </Button>
          )}
          {user && <NotificationBell />}
          <LanguageSwitcher />
          {user && (
            <Button variant="ghost" size="icon" className="h-9 w-9 text-white hover:bg-white/15 hover:text-white" onClick={async () => { await signOut(); navigate("/auth"); }} aria-label={t("common.signOut")}>
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};